import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Form, Button, Alert, Card, Row, Col, Spinner } from 'react-bootstrap'
import {
  useAdminPermission,
  useCreateAdminPermission,
  useUpdateAdminPermission,
  useDeleteAdminPermission
} from 'api/hooks/use-admin-permissions'
import { useAdminPermissionVariables } from 'api/hooks/use-admin-permission-variables'

const PermissionForm = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEditing = !!id

  const { data: permission, isLoading } = useAdminPermission({ id })
  const { data: permissionVariables } = useAdminPermissionVariables()
  const createMutation = useCreateAdminPermission({ options: { onSuccess: () => navigate('/admin/permissions') } })
  const updateMutation = useUpdateAdminPermission({ id, options: { onSuccess: () => navigate('/admin/permissions') } })
  const deleteMutation = useDeleteAdminPermission({ options: { onSuccess: () => navigate('/admin/permissions') } })

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    category_caption: '',
    is_high_risk: false,
    is_superuser: false,
    long_description: '',
    permission_variable_id: ''
  })

  useEffect(() => {
    if (permission) {
      setFormData({
        name: permission.name || '',
        description: permission.description || '',
        category: permission.category || '',
        category_caption: permission.category_caption || '',
        is_high_risk: permission.is_high_risk || false,
        is_superuser: permission.is_superuser || false,
        long_description: permission.long_description || '',
        permission_variable_id: permission.permission_variable_id || ''
      })
    }
  }, [permission])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (isEditing) {
      updateMutation.mutate(formData)
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this permission?')) {
      deleteMutation.mutate(id)
    }
  }

  const error = createMutation.error || updateMutation.error || deleteMutation.error
  const isSubmitting = createMutation.isLoading || updateMutation.isLoading || deleteMutation.isLoading

  if (isLoading && isEditing) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  return (
    <div>
      <h2>{isEditing ? 'Edit Permission' : 'Create Permission'}</h2>

      {error && <Alert variant="danger" className="mt-3">{error.message || 'An error occurred'}</Alert>}

      <Card className="mt-3">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control type="text" name="name" value={formData.name} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Category *</Form.Label>
                  <Form.Control type="text" name="category" value={formData.category} onChange={handleChange} required />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Category Caption</Form.Label>
                  <Form.Control type="text" name="category_caption" value={formData.category_caption} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description *</Form.Label>
              <Form.Control as="textarea" rows={2} name="description" value={formData.description} onChange={handleChange} required />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Long Description</Form.Label>
              <Form.Control as="textarea" rows={3} name="long_description" value={formData.long_description} onChange={handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Permission Variable</Form.Label>
              <Form.Select name="permission_variable_id" value={formData.permission_variable_id} onChange={handleChange}>
                <option value="">— None —</option>
                {(permissionVariables || []).map((pv) => (
                  <option key={pv.id} value={pv.id}>{pv.name}</option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">Master variable this permission maps to.</Form.Text>
            </Form.Group>

            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Check type="checkbox" name="is_high_risk" label="High Risk" checked={formData.is_high_risk} onChange={handleChange} />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Check type="checkbox" name="is_superuser" label="Superuser" checked={formData.is_superuser} onChange={handleChange} />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-between mt-4">
              <div>
                <Button type="submit" variant="primary" disabled={isSubmitting} className="me-2">
                  {isSubmitting ? 'Saving...' : isEditing ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="light-primary" onClick={() => navigate('/admin/permissions')} disabled={isSubmitting}>Cancel</Button>
              </div>
              {isEditing && (
                <Button type="button" variant="danger" onClick={handleDelete} disabled={isSubmitting}>Delete Permission</Button>
              )}
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  )
}

export default PermissionForm
